// FR-RD3 ("Camera overlay: circular or rectangular, draggable, resizable, mirrorable, with
// optional background blur") — the pure half. Everything here is math and lookup tables with
// no DOM, no canvas and no WebCodecs, so it is unit-tested directly (cameraFx.test.ts) the way
// ladder.ts is; compositor.ts is the thin IO wrapper that consumes it.

export type CameraFilterId = "none" | "background-blur" | "grayscale" | "warm" | "cool";

export interface CameraFilterOption {
  id: CameraFilterId;
  label: string;
  /** A `CanvasRenderingContext2D.filter` string applied to the whole camera image. `null`
   * means "leave ctx.filter alone" — used both by "none" and by "background-blur", whose blur
   * applies to the background copy only and is therefore issued by the compositor itself. */
  css: string | null;
  /** Whether this filter needs a person/background mask, i.e. the lazily-loaded segmenter. */
  segmented: boolean;
}

export const CAMERA_FILTERS: readonly CameraFilterOption[] = [
  { id: "none", label: "None", css: null, segmented: false },
  { id: "background-blur", label: "Background blur", css: null, segmented: true },
  { id: "grayscale", label: "Grayscale", css: "grayscale(1) contrast(1.05)", segmented: false },
  { id: "warm", label: "Warm", css: "sepia(0.28) saturate(1.25) contrast(1.03)", segmented: false },
  { id: "cool", label: "Cool", css: "saturate(1.1) hue-rotate(-12deg) brightness(1.04)", segmented: false },
];

export function cameraFilterOption(id: CameraFilterId): CameraFilterOption {
  return CAMERA_FILTERS.find((f) => f.id === id) ?? CAMERA_FILTERS[0];
}

export function filterNeedsSegmentation(id: CameraFilterId): boolean {
  return cameraFilterOption(id).segmented;
}

/** How hard to blur the background copy, as a fraction of the drawn camera image's height.
 * Expressed relatively so the bubble and a full-frame camera get a visually equal blur despite
 * being drawn at very different pixel sizes. */
export const BACKGROUND_BLUR_FRACTION = 0.055;

/** Feathering applied to the person mask before it is composited, same units as above. Without
 * it the segmenter's hard 0/1 category mask cuts a visible jagged edge around the subject. */
export const MASK_FEATHER_FRACTION = 0.012;

/**
 * The value `selfie_segmenter.tflite` writes into its category mask for the person.
 *
 * Measured, not assumed — and the assumption it replaces was wrong, which is worth recording
 * because the failure is invisible by inspection. The model is single-class: `getLabels()`
 * returns exactly `["selfie"]`, and the mask it emits is **0 for the person and 255 for the
 * background**, not the 0/1 category ids a two-class model would use. Reading it as "0 means
 * background" inverts the effect precisely — the subject is blurred and the room behind them
 * stays sharp — and that looks entirely deliberate on screen, so nothing short of a test
 * catches it. e2e/features/component/camera-effects.feature is that test: it runs the real
 * model over a real portrait and asserts the face is the sharp part.
 *
 * Lives here rather than beside the segmenter so compositor.ts can read it without a static
 * import of segmenter.ts — that module pulls in MediaPipe, which must stay in its own lazily
 * fetched chunk (see segmenter.ts).
 */
export const CATEGORY_PERSON = 0;

// ---- camera bubble placement (screen + camera mode) -------------------------------------

/** Where the camera bubble sits, in canvas-relative units so the values survive a resolution
 * change (switching the shared screen mid-session) without moving the bubble on screen.
 * `x`/`y` are the bubble's *center* as a fraction of canvas width/height; `radiusFraction` is
 * its radius as a fraction of canvas **height** (matching the pre-drag constant this replaces,
 * so a bubble is round rather than stretched on any aspect ratio). */
export interface CameraBubble {
  x: number;
  y: number;
  radiusFraction: number;
}

export const DEFAULT_BUBBLE_RADIUS_FRACTION = 0.16;
const DEFAULT_BUBBLE_MARGIN_FRACTION = 0.035;

/** The bottom-right resting place the overlay used before it became draggable — still the
 * starting position, so an untouched recording looks exactly as it did. `aspect` is canvas
 * width / height. */
export function defaultBubble(aspect: number): CameraBubble {
  const r = DEFAULT_BUBBLE_RADIUS_FRACTION;
  const margin = DEFAULT_BUBBLE_MARGIN_FRACTION;
  return clampBubble({ x: 1 - (r + margin) / aspect, y: 1 - (r + margin), radiusFraction: r }, aspect);
}

/** Keeps the whole bubble inside the frame. The horizontal half-extent is `radiusFraction /
 * aspect` because the radius is measured against canvas *height* — on a 16:9 canvas a bubble
 * 0.16 of the height is only 0.09 of the width. A bubble too large to fit is centered on that
 * axis rather than clamped to a contradictory range. */
export function clampBubble(bubble: CameraBubble, aspect: number): CameraBubble {
  const safeAspect = aspect > 0 ? aspect : 1;
  const r = Math.min(0.5, Math.max(0.04, bubble.radiusFraction));
  const halfW = r / safeAspect;
  const clamp = (value: number, half: number) =>
    half >= 0.5 ? 0.5 : Math.min(1 - half, Math.max(half, value));
  return { x: clamp(bubble.x, halfW), y: clamp(bubble.y, r), radiusFraction: r };
}

// ---- pointer -> canvas mapping ------------------------------------------------------------

export interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Where an `object-fit: cover` media element actually paints inside its box. The preview
 * `<video>` is cover-cropped by recorder.css, so a pointer position is only the same fraction
 * of the recorded canvas after the crop is undone — without this, dragging the bubble on a
 * non-16:9 screen capture puts it somewhere else in the recording than under the cursor. */
export function coverRect(containerW: number, containerH: number, mediaW: number, mediaH: number): Rect {
  if (mediaW <= 0 || mediaH <= 0) return { left: 0, top: 0, width: containerW, height: containerH };
  const scale = Math.max(containerW / mediaW, containerH / mediaH);
  const width = mediaW * scale;
  const height = mediaH * scale;
  return { left: (containerW - width) / 2, top: (containerH - height) / 2, width, height };
}

/** Pointer coordinates (already relative to the container's top-left) to a 0..1 fraction of the
 * *media*, undoing the cover crop. Values outside the media clamp to its edges. */
export function pointerToMediaFraction(
  offsetX: number,
  offsetY: number,
  container: { width: number; height: number },
  mediaW: number,
  mediaH: number,
): { x: number; y: number } {
  const rect = coverRect(container.width, container.height, mediaW, mediaH);
  const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
  return {
    x: rect.width > 0 ? clamp01((offsetX - rect.left) / rect.width) : 0,
    y: rect.height > 0 ? clamp01((offsetY - rect.top) / rect.height) : 0,
  };
}

/** Inverse of `pointerToMediaFraction` — where a canvas-relative point lands inside the
 * cover-cropped container, so the drag handle can be drawn exactly over the composited bubble.
 * Returned in container pixels. */
export function mediaFractionToPointer(
  fx: number,
  fy: number,
  container: { width: number; height: number },
  mediaW: number,
  mediaH: number,
): { x: number; y: number } {
  const rect = coverRect(container.width, container.height, mediaW, mediaH);
  return { x: rect.left + fx * rect.width, y: rect.top + fy * rect.height };
}

/** The on-screen radius, in container pixels, of a bubble whose radius is `radiusFraction` of
 * the canvas height. Same cover-crop correction as the position mapping above. */
export function bubbleScreenRadius(
  radiusFraction: number,
  container: { width: number; height: number },
  mediaW: number,
  mediaH: number,
): number {
  return radiusFraction * coverRect(container.width, container.height, mediaW, mediaH).height;
}
